import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { logger } from "~/common/logger";

interface DecodedToken {
  postId: string;
  userId: string;
  expiration: number;
}

const decodeGiftToken = (token: string): DecodedToken | null => {
  try {
    const decoded = Buffer.from(token, "base64").toString("utf8");
    const parts = decoded.split("|");
    if (parts.length !== 3) return null;

    const [postId, userId, expirationStr] = parts;
    const expiration = parseInt(expirationStr, 10);
    if (Number.isNaN(expiration)) return null;

    return { postId, userId, expiration };
  } catch (error) {
    logger.error("Error decoding gift token:", error);
    return null;
  }
};

/**
 * Hook to validate if the current route is accessed via a valid gift link.
 * Returns { isGiftValid, isGiftExpired, isValidationComplete }
 */
export const useGiftValidation = (
  pagePostId?: number,
  shouldValidate = true
) => {
  const { query, isReady } = useRouter();
  const [validationState, setValidationState] = useState<{
    isComplete: boolean;
    isGiftValid: boolean;
    isGiftExpired: boolean;
  }>({ isComplete: false, isGiftValid: false, isGiftExpired: false });

  const tokenParam = query.t as string | undefined;
  const hashParam = query.h as string | undefined;

  useEffect(() => {
    if (!isReady) {
      setValidationState({
        isComplete: false,
        isGiftValid: false,
        isGiftExpired: false,
      });
      return;
    }

    if (!shouldValidate) {
      setValidationState({
        isComplete: true,
        isGiftValid: false,
        isGiftExpired: false,
      });
      return;
    }

    const checkGiftValidation = async () => {
      try {
        const decodedToken = decodeGiftToken(tokenParam!);
        if (!decodedToken) {
          logger.error("Invalid token format");
          setValidationState({
            isComplete: true,
            isGiftValid: false,
            isGiftExpired: true,
          });
          return;
        }

        const currentTime = Math.floor(Date.now() / 1000);
        if (currentTime >= decodedToken.expiration) {
          logger.error("Gift token has expired");
          setValidationState({
            isComplete: true,
            isGiftValid: false,
            isGiftExpired: true,
          });
          return;
        }

        if (pagePostId) {
          const currentPostId = pagePostId.toString();
          if (currentPostId !== decodedToken.postId) {
            logger.error("Token postId does not match current page postId");
            setValidationState({
              isComplete: true,
              isGiftValid: false,
              isGiftExpired: true,
            });
            return;
          }
        }

        const requestUrl = window.location.pathname + window.location.search;
        const response = await fetch(requestUrl, { method: "HEAD" });
        const giftValidatedHeader = response.headers.get("x-gift-validated");
        const isGiftValid = giftValidatedHeader === "true";

        setValidationState({
          isComplete: true,
          isGiftValid,
          isGiftExpired: !isGiftValid,
        });
      } catch (error) {
        logger.error("Error checking gift validation:", error);
        setValidationState({
          isComplete: true,
          isGiftValid: false,
          isGiftExpired: true,
        });
      }
    };

    checkGiftValidation();
  }, [isReady, shouldValidate, tokenParam, hashParam, pagePostId]);

  return {
    isGiftValid: validationState.isGiftValid,
    isGiftExpired: validationState.isGiftExpired,
    isValidationComplete: validationState.isComplete,
  };
};
